import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class AudioService {
  /**
   * Converts base64 PCM audio to WAV and returns a Blob URL
   */
  public base64PcmToWav(base64: string, sampleRate = 44100, numChannels = 1): string {
    const raw = atob(base64);
    const buffer = new ArrayBuffer(44 + raw.length);
    const view = new DataView(buffer);

    this.writeWavHeader(view, raw.length, sampleRate, numChannels);

    // Write PCM data
    for (let i = 0; i < raw.length; i++) {
      view.setUint8(44 + i, raw.charCodeAt(i));
    }

    const blob = new Blob([view], { type: 'audio/wav' });
    return URL.createObjectURL(blob);
  }

  public resampleBuffer(input: Float32Array, inputRate: number, outputRate: number): Float32Array {
    if (inputRate === outputRate) return input;
    const ratio = outputRate / inputRate;
    const newLength = Math.round(input.length * ratio);
    const out = new Float32Array(newLength);
    for (let i = 0; i < newLength; i++) {
      const origin = i / ratio;
      const low = Math.floor(origin);
      const high = Math.min(low + 1, input.length - 1);
      const t = origin - low;
      out[i] = (1 - t) * input[low] + t * input[high];
    }
    return out;
  }

  public floatTo16BitPCM(float32Array: Float32Array): Uint8Array {
    const buffer = new ArrayBuffer(float32Array.length * 2);
    const view = new DataView(buffer);
    let offset = 0;
    for (let i = 0; i < float32Array.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return new Uint8Array(buffer);
  }

  public uint8ToBase64(bytes: Uint8Array): string {
    const CHUNK_SIZE = 0x8000; // 32KB
    let binary = '';
    for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
      const sub = bytes.subarray(i, i + CHUNK_SIZE);
      binary += String.fromCharCode(...(sub as unknown as number[]));
    }
    return btoa(binary);
  }

  public chunkUint8Array(bytes: Uint8Array, chunkSize: number): Uint8Array[] {
    const chunks: Uint8Array[] = [];
    for (let i = 0; i < bytes.length; i += chunkSize) {
      chunks.push(bytes.subarray(i, Math.min(i + chunkSize, bytes.length)));
    }
    return chunks;
  }

  /** -------------------- HELPERS -------------------- **/

  private writeWavHeader(
    view: DataView,
    dataLength: number,
    sampleRate: number,
    numChannels: number
  ) {
    const byteRate = sampleRate * numChannels * 2;

    this.writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataLength, true);
    this.writeString(view, 8, 'WAVE');
    this.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); // Subchunk1Size
    view.setUint16(20, 1, true); // PCM format
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, numChannels * 2, true); // BlockAlign
    view.setUint16(34, 16, true); // Bits per sample
    this.writeString(view, 36, 'data');
    view.setUint32(40, dataLength, true);
  }

  private writeString(view: DataView, offset: number, str: string) {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  }
}
